import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { ApiResponse } from '../../shared/types';
import { Lane, LaneApiPayload } from '../models/lane.model';

export interface DeviceConnectionStatus {
  connected: boolean;
  lastConnectedAt?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class LaneService {
  constructor(private apiService: ApiService) {}

  private lanesPath(companyId: string, locationId: string, laneId?: string): string {
    const base = `admin/super-admin/companies/${companyId}/locations/${locationId}/lanes`;
    return laneId ? `${base}/${laneId}` : base;
  }

  listLanes(companyId: string, locationId: string): Observable<ApiResponse<Lane[]>> {
    return this.apiService.getWithSuperAdminToken<Lane[]>(this.lanesPath(companyId, locationId)).pipe(
      catchError(error => {
        console.error('Error fetching lanes:', error);
        return throwError(() => error);
      })
    );
  }

  getLane(companyId: string, locationId: string, laneId: string): Observable<ApiResponse<Lane>> {
    return this.apiService.getWithSuperAdminToken<Lane>(this.lanesPath(companyId, locationId, laneId)).pipe(
      catchError(error => {
        console.error('Error fetching lane:', error);
        return throwError(() => error);
      })
    );
  }

  createLane(
    companyId: string,
    locationId: string,
    body: LaneApiPayload
  ): Observable<ApiResponse<Lane>> {
    return this.apiService.postWithSuperAdminToken<Lane>(this.lanesPath(companyId, locationId), body).pipe(
      catchError(error => {
        console.error('Error creating lane:', error);
        return throwError(() => error);
      })
    );
  }

  updateLane(
    companyId: string,
    locationId: string,
    laneId: string,
    body: LaneApiPayload
  ): Observable<ApiResponse<Lane>> {
    return this.apiService
      .patchWithSuperAdminToken<Lane>(this.lanesPath(companyId, locationId, laneId), body)
      .pipe(
        catchError(error => {
          console.error('Error updating lane:', error);
          return throwError(() => error);
        })
      );
  }

  deleteLane(companyId: string, locationId: string, laneId: string): Observable<ApiResponse<any>> {
    return this.apiService.deleteWithSuperAdminToken(this.lanesPath(companyId, locationId, laneId)).pipe(
      catchError(error => {
        console.error('Error deleting lane:', error);
        return throwError(() => error);
      })
    );
  }

  checkDeviceConnection(serialNumber: string): Observable<ApiResponse<DeviceConnectionStatus>> {
    return this.apiService
      .getWithSuperAdminToken<DeviceConnectionStatus>(`admin/super-admin/devices/status?serial=${encodeURIComponent(serialNumber)}`)
      .pipe(
        catchError(error => {
          console.error('Error checking device connection:', error);
          return throwError(() => error);
        })
      );
  }
}
